import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { useNuxtApp, useRuntimeConfig, navigateTo } from '#imports'
import { useAgentAuthStore } from '~/stores/agentAuth'

type AgentRequestConfig = AxiosRequestConfig & {
  requireCsrf?: boolean
}

let csrfPromise: Promise<void> | null = null

// 检查是否存在
function hasCsrfCookie(): boolean {
  if (process.server) return false
  return document.cookie.includes('XSRF-TOKEN=')
}

export function useAgentApi() {
  const { $api } = useNuxtApp()
  const auth = useAgentAuthStore()

  async function ensureCsrf() {
    if (process.server) return
    if (hasCsrfCookie()) return // 有了就不打，解决偶尔传输 419 的核心！

    if (!csrfPromise) {
      csrfPromise = (async () => {
        // 直接用配置好的 $api 去打，带上 withCredentials 才能种下 Cookie
        await $api.get('/agent/csrf-cookie')
      })().finally(() => {
        csrfPromise = null
      })
    }
    return csrfPromise
  }

  function handleError(error: any): never {
    const status = error?.response?.status
    if (status === 401 || status === 419) {
      if (process.client) {
        auth.clearAgent()
        navigateTo('/agent/login')
      }
    }
    throw error
  }

  async function get<T = any>(url: string, cfg: AgentRequestConfig = {}): Promise<AxiosResponse<T>> {
    try {
      return await $api.get<T>(url, cfg)
    } catch (error: any) { handleError(error) }
  }

  async function post<T = any>(url: string, data?: any, cfg: AgentRequestConfig = {}): Promise<AxiosResponse<T>> {
    const { requireCsrf = true, ...axiosCfg } = cfg
    try {
      if (requireCsrf) await ensureCsrf()
      return await $api.post<T>(url, data, axiosCfg)
    } catch (error: any) { handleError(error) }
  }

  async function put<T = any>(url: string, data?: any, cfg: AgentRequestConfig = {}): Promise<AxiosResponse<T>> {
    const { requireCsrf = true, ...axiosCfg } = cfg
    try {
      if (requireCsrf) await ensureCsrf()
      return await $api.put<T>(url, data, axiosCfg)
    } catch (error: any) { handleError(error) }
  }

  async function del<T = any>(url: string, cfg: AgentRequestConfig = {}): Promise<AxiosResponse<T>> {
    const { requireCsrf = true, ...axiosCfg } = cfg
    try {
      if (requireCsrf) await ensureCsrf()
      return await $api.delete<T>(url, axiosCfg)
    } catch (error: any) { handleError(error) }
  }

  return { ensureCsrf, get, post, put, del }
}
